import classNames from 'classnames'
import Link from 'next/link'
import { type FC, type MouseEventHandler, type ReactNode } from 'react'

type ButtonProps = {
  className?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  onDoubleClick?: MouseEventHandler<HTMLButtonElement>
  href?: string
  color?: 'emerald' | 'blue' | 'red' | 'gray'
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'outline'
  fullWidth?: boolean
  disabled?: boolean
  align?: 'center' | 'left'
  id?: string
  type?: 'button' | 'submit'
  'aria-label'?: string
  title?: string
  children: ReactNode
}

const Button: FC<ButtonProps> = ({
  href,
  onClick,
  onDoubleClick,
  children,
  color = 'emerald',
  size = 'md',
  variant = 'solid',
  fullWidth = true,
  className = '',
  disabled = false,
  align = 'center',
  id,
  type = 'button',
  'aria-label': ariaLabel,
  title,
}) => {
  const buttonClassName = classNames(
    'font-medium transition-all duration-200 rounded-md flex items-center justify-center',
    {
      // Size variants
      'text-xs py-1 px-3': size === 'sm',
      'text-sm py-1.5 px-4': size === 'md',
      'text-base py-2 px-5': size === 'lg',

      // Width variants
      'w-full max-w-xs': fullWidth,
      'inline-flex': !fullWidth,

      // Color and style variants - Solid
      'bg-brand-secondary hover:opacity-90 text-white':
        variant === 'solid' && color === 'emerald',
      'bg-info hover:opacity-90 text-white': variant === 'solid' && color === 'blue',
      'bg-danger hover:opacity-90 text-white': variant === 'solid' && color === 'red',
      'bg-gray-500 hover:bg-gray-600 text-white': variant === 'solid' && color === 'gray',

      // Color and style variants - Outline
      'border border-brand-secondary text-brand-secondary hover:bg-brand-secondary-subtle':
        variant === 'outline' && color === 'emerald',
      'border border-info text-info hover:bg-info-subtle':
        variant === 'outline' && color === 'blue',
      'border border-danger text-danger hover:bg-danger-subtle':
        variant === 'outline' && color === 'red',
      'border   ':
        variant === 'outline' && color === 'gray',

      // Alignment
      'text-center': align === 'center',
      'text-left': align === 'left',

      // Disabled state
      'opacity-60 cursor-not-allowed': disabled,

      // Custom class
      [className]: className,
    },
  )

  if (href) {
    return (
      <Link
        href={href}
        className={buttonClassName}
        aria-label={ariaLabel}
        title={title}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      id={id}
      disabled={disabled}
      className={buttonClassName}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      type={type}
      aria-label={ariaLabel}
      title={title}
    >
      {children}
    </button>
  )
}

export default Button
